/**
 * Qualtrics Website / App Feedback — Advanced Event Tracking + Embedded Data.
 * @see https://www.qualtrics.com/support/website-app-feedback/intercepts-tab/edit-intercept-section/action-set-options/adding-event-tracking-triggering/
 *
 * SPA-style chat: embedded data and DOM are not ready at first page load. After thumbs feedback,
 * re-run intercept evaluation so Qualtrics sees current state (Qualtrics SPA pattern).
 * @see https://www.qualtrics.com/support/website-app-feedback/common-use-cases/single-page-application/
 *
 * Event queue + evaluation: Qualtrics documents `load().then(run)` when `load` returns a promise.
 * **Order:** call `unload()` first, then set `window._qLastMessageId` / `_qsie`, then `load` → `run`.
 * Setting `_qsie` before `unload()` can drop events — `unload()` resets deployment state and may clear
 * the event queue so only the first interaction appears to fire.
 *
 * **One event per click:** Assign `_qsie = [eventName]` (override the queue); do not `push`, so each run
 * carries only the event for the button that was just clicked.
 *
 * **Thumb event counts:** `_qsie` holds only the latest click, but Qualtrics also keeps
 * `EventTracker.counts`. Intercepts that use `(ThumbsUp ≥ 1) OR (ThumbsDown ≥ 1)` will keep matching
 * the first branch once both counts went up (e.g. dislike then like → “like” creative every time).
 * Clear **only** `ThumbsUpClicked` / `ThumbsDownClicked`: once **after `unload()`** and **before**
 * assigning `_qsie` (fresh slate for this click), and again **immediately before `run()`** because
 * `load()` may restore counts from persisted Qualtrics client state.
 *
 * **`window._qThumbDirection`:** Set to `'up'` | `'down'` on each notify so Qualtrics embedded data
 * can read the current click (in addition to `_qsie` event names).
 *
 * Enabled only on mi-chat-renderer AI chat route so intercepts do not fire from other hosts/paths.
 */
const THUMB_UP_EVENT = 'ThumbsUpClicked';
const THUMB_DOWN_EVENT = 'ThumbsDownClicked';

export function isQualtricsChatFeedbackPathEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return /\/ai\/chat/i.test(window.location.pathname);
}

declare global {
  interface Window {
    _qLastMessageId?: string;
    _qsie?: string[];
    /** Current thumb click — use in Qualtrics embedded data (JavaScript): `window._qThumbDirection`. */
    _qThumbDirection?: 'up' | 'down';
    QSI?: {
      API?: {
        unload?: () => void;
        /** May return a Promise; Qualtrics examples use `load().then(run)`. */
        load?: () => void | PromiseLike<void>;
        run?: () => void;
        Events?: {
          count?: (eventName: string) => number;
        };
      };
      EventTracker?: {
        counts?: Record<string, number>;
      };
    };
  }
}

/** Clears only thumb Advanced Event Tracking counters so OR-based intercept logic matches the current `_qsie` event. */
function resetQualtricsThumbEventCounts(): void {
  const counts = window.QSI?.EventTracker?.counts;
  if (!counts) return;
  delete counts[THUMB_UP_EVENT];
  delete counts[THUMB_DOWN_EVENT];
}

/**
 * Sets embedded-data source for Qualtrics ("Value from JavaScript" → window._qLastMessageId),
 * Queues **exactly one** Advanced Event Tracking event on `_qsie`, then SPA-reinits: unload → load → run.
 */
export function notifyQualtricsThumbsFeedback(direction: 'up' | 'down', sessionId: string, messageId: string): void {
  if (!isQualtricsChatFeedbackPathEnabled()) return;

  const embeddedKey = `${sessionId}_msg-${messageId}`;
  const eventName = direction === 'up' ? THUMB_UP_EVENT : THUMB_DOWN_EVENT;

  window._qThumbDirection = direction;

  const api = window.QSI?.API;
  if (api?.unload && api?.load && api?.run) {
    const unload = api.unload;
    const load = api.load;
    const run = api.run;

    unload();
    resetQualtricsThumbEventCounts();
    window._qLastMessageId = embeddedKey;
    window._qsie = [eventName];

    const loadResult = load();
    void Promise.resolve(loadResult).then(() => {
      run();
    });
    return;
  }

  // Deployment script not ready: single-event queue for processing on first load (Event Usage doc).
  window._qLastMessageId = embeddedKey;
  window._qsie = [eventName];
}
